import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/i18nContext';
import { isAgent } from '../constants/roles';
import { PRIORITIES, priorityKey } from '../constants/priority';

const NewCaseModal = ({ onClose, onCreated, isDark }) => {
  const { api, user } = useAuth();
  const { t, language } = useI18n();
  const staff = isAgent(user?.role);

  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority]       = useState('normal');
  const [files, setFiles]             = useState([]);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');

  // Ügyfélválasztó (csak staffnak)
  const [customers, setCustomers]     = useState([]);
  const [search, setSearch]           = useState('');
  const [customerId, setCustomerId]   = useState('');
  const [customerLang, setCustomerLang] = useState('');

  // Ingatlan
  const [properties, setProperties]   = useState([]);
  const [propertyId, setPropertyId]   = useState('');

  // Saját ingatlanok betöltése (customer esetén)
  useEffect(() => {
    if (staff) return;
    api.get('/properties')
      .then(r => {
        const props = r.data || [];
        setProperties(props);
        if (props.length === 1) setPropertyId(String(props[0].id));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Kiválasztott customer ingatlanainak betöltése (staff esetén)
  useEffect(() => {
    if (!staff || !customerId) { setProperties([]); setPropertyId(''); return; }
    api.get(`/properties?user_id=${customerId}`)
      .then(r => {
        const props = r.data || [];
        setProperties(props);
        if (props.length === 1) setPropertyId(String(props[0].id));
        else setPropertyId('');
      })
      .catch(() => { setProperties([]); setPropertyId(''); });
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!staff) return;
    const t2 = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cases/customers?q=${encodeURIComponent(search)}`);
        setCustomers(data);
      } catch { setCustomers([]); }
    }, 300);
    return () => clearTimeout(t2);
  }, [search, staff]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectCustomer = (c) => {
    setCustomerId(c.id);
    setSearch(`${c.username} (${c.email})`);
    setCustomerLang(c.preferred_language || '');
    setCustomers([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (staff && !customerId) { setErr(t('cases.customerRequired')); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        priority,
        language: staff ? (customerLang || 'en') : language,
        ...(staff && customerId ? { customer_id: customerId } : {}),
        ...(propertyId ? { property_id: propertyId } : {}),
      };
      const { data: created } = await api.post('/cases', payload);
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/cases/${created.id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onCreated?.(created);
    } catch (e2) {
      setErr(e2.response?.data?.error || t('cases.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`cs-modal-overlay ${isDark ? 'theme-dark' : 'theme-light'}`} onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="cs-modal-title">{t('cases.newCase')}</h2>
        {err && <div className="cs-modal-error">{err}</div>}
        <form onSubmit={submit} className="cs-modal-form">

          {/* Ügyfélválasztó — csak staffnak */}
          {staff && (
            <label className="cs-field" style={{ position: 'relative' }}>
              <span>{t('cases.onBehalfOf')}</span>
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCustomerId(''); setCustomerLang(''); }}
                placeholder={t('cases.searchCustomer')}
                autoComplete="off"
              />
              {customers.length > 0 && (
                <ul className="cs-customer-dropdown">
                  {customers.map(c => (
                    <li key={c.id} onClick={() => selectCustomer(c)}>
                      <strong>{c.username}</strong>
                      <span>{c.first_name} {c.last_name} · {c.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </label>
          )}

          {/* Ingatlan — ha van legalább 1 */}
          {properties.length > 0 && (
            <label className="cs-field">
              <span>🏠 {t('admin.properties') || 'Property'}</span>
              {properties.length === 1 ? (
                <div className="cs-property-single">
                  {properties[0].name}
                  {properties[0].address_city && ` — ${properties[0].address_city}`}
                </div>
              ) : (
                <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                  <option value="">— {t('cases.selectProperty') || 'Select property'} —</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.address_city ? ` — ${p.address_city}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </label>
          )}

          <label className="cs-field">
            <span>{t('cases.titleLabel')}</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} autoFocus={!staff} required />
          </label>
          <label className="cs-field">
            <span>{t('cases.descriptionLabel')}</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </label>
          <label className="cs-field">
            <span>{t('priority.label')}</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map(p => <option key={p} value={p}>{t(priorityKey(p))}</option>)}
            </select>
          </label>
          <label className="cs-field">
            <span>{t('cases.attachments')}</span>
            <input type="file" multiple accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFiles(Array.from(e.target.files))} />
            {files.length > 0 && <small className="cs-file-count">{files.length} {t('cases.filesSelected')}</small>}
          </label>
          <div className="cs-modal-actions">
            <button type="button" className="cs-btn-ghost" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="cs-btn-primary" disabled={saving || !title.trim() || (staff && !customerId)}>
              {saving ? t('cases.creating') : t('cases.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCaseModal;
